import { css, html } from 'react-strict-dom';
import { Api } from 'src/api';
import { ProgressBar, Text, TimerControls } from 'src/components';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { useTimer } from 'src/contexts';
import { Model } from 'src/models';

type Props = {
  company: Api.Company.BaseView;
  numOpenTickets: number;
  maintenanceMinutesLogged: number | null;
};

/**
 * The top row of a client card: who they are, what the arrangement is, and the
 * timer for time on the client itself rather than any one piece of work.
 */
export default function ProjectsCompanyHeader({
  company,
  numOpenTickets,
  maintenanceMinutesLogged,
}: Props) {
  const target = Model.TimerTarget.fromCompany(company);
  const running = useTimer().isRunning(target);
  const maintenance = Model.Company.getMaintenance(
    company,
    maintenanceMinutesLogged
  );

  return (
    <html.div
      style={[Style.alignCenter, styles.header, running && styles.running]}
    >
      <html.div style={[Style.row, Style.alignCenter, styles.identity]}>
        <html.img
          style={styles.logo}
          src={`/img/companies/${company.slug}.png`}
          alt={company.name}
        />
        <html.div style={[Style.col, styles.label]}>
          <Text typeface="body.medium" color="neutral100">
            {company.name}
          </Text>
          <Text typeface="caption.medium" color="neutral60">
            {Model.Company.getProjectsMeta(company, numOpenTickets)}
          </Text>
          {maintenance !== null && (
            <html.div style={styles.maintenance}>
              <ProgressBar
                progress={maintenance.progress}
                label={maintenance.label}
              />
            </html.div>
          )}
        </html.div>
      </html.div>

      <TimerControls target={target} style={styles.controls} />
    </html.div>
  );
}

const styles = css.create({
  header: {
    display: 'flex',
    gap: 12,
    paddingBlock: 14,
    paddingInline: 16,
    // Follows the card's own corners, which are left unclipped so the category
    // menu can open past them.  One less than the card's radius, for its border.
    borderTopLeftRadius: 11,
    borderTopRightRadius: 11,
    flexDirection: {
      default: 'row',
      '@media (max-width: 640px)': 'column',
    },
  },
  running: {
    backgroundColor: color.greenBg,
  },
  identity: {
    gap: 12,
    flexGrow: 1,
    minWidth: 0,
    alignSelf: {
      default: 'auto',
      '@media (max-width: 640px)': 'stretch',
    },
  },
  logo: {
    flexShrink: 0,
    width: 24,
    height: 24,
    borderRadius: 4,
    objectFit: 'cover',
  },
  label: {
    flexGrow: 1,
    minWidth: 0,
  },
  maintenance: {
    paddingTop: 6,
  },
  // Stacked below the identity, the controls take the width and spread out.
  controls: {
    justifyContent: {
      default: 'flex-start',
      '@media (max-width: 640px)': 'space-between',
    },
  },
});
